const DefaultGID = "G-8W5LR442LD";

/**
 * The fetchAnalyticsID function retrieves both Client ID and Session ID.
 * If either value does not return within 1 second (e.g., due to an ad blocker),
 * the function returns without error, providing empty strings for missing values.
 */
export function getStreamAnalyticId(
  GID: string = DefaultGID,
  timeout: number = 1000
): Promise<[string, string]> {
  if (!window.REACT_APP_ANALYTICS_ENABLED || !window.gtag || typeof window.gtag !== "function") {
    return Promise.resolve(["", ""]); // Return empty strings if gtag is not available
  }
  const timeoutPromise = new Promise<string>((resolve) => {
    setTimeout(() => {
      console.warn("Operation timed out");
      resolve(""); // Resolve with empty string on timeout
    }, timeout);
  });
  const fetchClientID = new Promise<string>((resolve) => {
    if (!window.gtag || typeof window.gtag !== "function") {
      return resolve(""); // Return empty strings if gtag is not available
    }
    // Retrieve the Client ID from Google Analytics
    window.gtag("get", GID, "client_id", (clientID: string) => {
      resolve(clientID);
    });
  });
  const fetchSessionID = new Promise<string>((resolve) => {
    if (!window.gtag || typeof window.gtag !== "function") {
      resolve("");
      return;
    }
    // Retrieve the Session ID from Google Analytics
    window.gtag("get", GID, "session_id", (sessionID: string) => {
      resolve(sessionID);
    });
  });
  return Promise.all([
    Promise.race([fetchClientID, timeoutPromise]),
    Promise.race([fetchSessionID, timeoutPromise]),
  ]);
}

export function setStreamAnalyticsId(
  clientId: string,
  sessionId: string,
  GID: string = DefaultGID
): void {
  if (!window.gtag || typeof window.gtag !== "function") {
    console.warn("gtag is not available, cannot set analytics ID");
    return;
  }
  // Set the Client ID in Google Analytics
  window.gtag("set", GID, "client_id", clientId);

  // Set the Session ID in Google Analytics
  window.gtag("set", GID, "session_id", sessionId);
}

/**
 * Public interface to retrieve the analytics ID.
 * This function fetches the Client ID and Session ID from Google Analytics.
 * If either value does not return within 1 second, it returns empty strings.
 * @returns
 */
export function getAnalyticsId(): Promise<[string, string]> {
  return getStreamAnalyticId(DefaultGID, 1000);
}

/**
 * Public interface to set the analytics ID.
 * This function sets the Client ID and Session ID in Google Analytics.
 * @param clientId
 * @param sessionId
 */
export function setAnalyticsId(clientId: string, sessionId: string): void {
  setStreamAnalyticsId(clientId, sessionId, DefaultGID);
}
